import {
  computed,
  inject,
  Injectable,
  resource,
  ResourceRef,
  signal,
} from '@angular/core';
import { ProduktType } from '@features/produktlister/produkt-type.model';
import dayjs from 'dayjs';
import { ProduktPris } from '@features/produktlister/produkt-pris.model';
import { SimpleApi } from '@core/api/simple.api';

@Injectable({
  providedIn: 'root',
})
export class ProduktlisterService {
  simpleApi = inject(SimpleApi);
  currentYear = dayjs().year();

  aar = signal(this.currentYear);
  produktType = signal<ProduktType | undefined>(undefined);
  opkraevningsfrekvens = signal<string | undefined>(undefined);
  skattekode = signal<number | undefined>(undefined);
  prisgruppe = signal<number | undefined>(undefined);
  praemiefritagelse = signal<boolean>(false);
  foedselsdato = signal<Date | undefined>(undefined);
  bidrag = signal<number | undefined>(undefined);
  daekningsstoerrelse = signal<number | undefined>(undefined);
  beregnetPris = signal<number | undefined>(undefined);

  paramsValid = computed(() => {
    if (!this.aar() || !this.produktType()) {
      return false;
    }

    if (
      this.produktType()?.type == 'lts' ||
      this.produktType()?.type == 'standalone'
    ) {
      if (this.foedselsdatoRequired() && !this.foedselsdato()) {
        return false;
      }
      if (this.bidragRequired() && !this.bidrag()) {
        return false;
      }
      if (
        !this.opkraevningsfrekvens() ||
        !this.skattekode() ||
        !this.prisgruppe()
      ) {
        return false;
      }
    }

    return true;
  });

  produktPriser = resource({
    params: () => {
      const produktRammeId = this.produktType()?.id;
      if (produktRammeId) {
        return {
          aar: this.aar(),
          produktRammeId: produktRammeId,
        };
      }
      return undefined;
    },
    loader: async ({ params, abortSignal }) => {
      const response = await this.simpleApi.getList<ProduktPris>(
        '/api/admin/produktlister/produktpriser',
        {
          ...params,
        },
        abortSignal
      );

      this.beregnetPris.set(undefined);

      const opkraevningsfrekvenser = new Set(
        response.map(x => x.opkraevningsfrekvens)
      );
      if (opkraevningsfrekvenser.size == 1) {
        this.opkraevningsfrekvens.set([...opkraevningsfrekvenser][0]);
      }

      const skattekoder = new Set(response.map(x => x.skattekode));
      if (skattekoder.size == 1) {
        this.skattekode.set([...skattekoder][0]);
      }

      const prisgrupper = new Set(response.map(x => x.prisgruppe));
      if (prisgrupper.size == 1) {
        this.prisgruppe.set([...prisgrupper][0]);
      }

      return response;
    },
  });

  valgtProduktPris: ResourceRef<ProduktPris | undefined> = resource({
    params: () => {
      if (!this.paramsValid()) {
        return undefined;
      }
      if (this.produktType()?.type === 'rap') {
        return undefined;
      }
      return {
        aar: this.aar(),
        produktRammeId: this.produktType()?.id,
        opkraevningsfrekvens: this.opkraevningsfrekvens(),
        skattekode: this.skattekode(),
        prisgruppe: this.prisgruppe(),
        foedselsdato: this.foedselsdato()
          ? dayjs(this.foedselsdato()).format('YYYY-MM-DD')
          : undefined,
        bidrag: this.bidrag(),
        lts: this.produktType()?.type == 'lts',
      };
    },
    loader: async ({ params, abortSignal }) => {
      this.beregnetPris.set(undefined);
      this.daekningsstoerrelse.set(undefined);
      return await this.simpleApi.getSingle<ProduktPris>(
        '/api/admin/produktlister/produktpris',
        {
          ...params,
        },
        abortSignal
      );
    },
  });

  foedselsdatoRequired = computed(() => {
    const priser: ProduktPris[] = this.produktPriser.value() || [];
    return new Set(priser.map(x => x.minAlder)).size > 1;
  });

  bidragRequired = computed(() => {
    const priser: ProduktPris[] = this.produktPriser.value() || [];
    return new Set(priser.map(x => x.minBidrag)).size > 1;
  });

  initialize() {
    this.aar.set(this.currentYear);
    this.produktType.set(undefined);
  }

  async beregnPris() {
    const pris = await this.simpleApi.getSingle<number>(
      '/api/admin/produktlister/beregnpris',
      {
        produktPrisId: this.valgtProduktPris.value()?.id,
        daekningsstoerrelse: this.daekningsstoerrelse(),
        praemiefritagelse: this.praemiefritagelse(),
      }
    );
    this.beregnetPris.set(pris);
  }
}
